import { useState } from 'react'

import { Bag, Bars, House, Settings, Smiley, Objects } from '~/ui'
import { Slot, Slottable } from '~/ui/slot'
import { cva } from '~/utils/classnames'

const navbarLinkStyle = cva({
  base: [
    'flex h-9 w-full items-center gap-2 rounded-lg p-2.5 whitespace-nowrap cursor-pointer',
    'text-foreground/80 hover:bg-foreground/4 hover:text-highlight active:bg-foreground/6 active:text-highlight text-sm font-medium',
    'data-[current-page=true]:bg-foreground/8 data-[current-page=true]:text-highlight',
  ],
  variants: {
    isActive: {
      true: 'bg-foreground/8 text-highlight',
      false: '',
    },
  },
})

const Icons = {
  home: House,
  orders: Bag,
  settings: Settings,
  analytics: Bars,
  account: Smiley,
  objects: Objects,
}

export type NavbarButtonIcon = keyof typeof Icons

interface NavbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: NavbarButtonIcon
  isActive?: boolean
  asChild?: boolean
}

export const NavbarButton = ({
  isActive = false,
  asChild,
  children,
  className,
  icon,
  onMouseEnter,
  onMouseLeave,
  ...props
}: NavbarButtonProps) => {
  const [hover, setHover] = useState(false)
  const Icon = icon ? Icons[icon] : null

  const Component = asChild ? Slot : 'button'

  return (
    <Component
      className={navbarLinkStyle({ className, isActive })}
      onMouseEnter={(e) => {
        onMouseEnter?.(e)
        if (e.defaultPrevented) return

        setHover(true)
      }}
      onMouseLeave={(e) => {
        onMouseLeave?.(e)
        if (e.defaultPrevented) return

        setHover(false)
      }}
      {...props}
    >
      <Slottable asChild={!!asChild} child={children}>
        {(child) => (
          <>
            {Icon && (
              <Icon className="shrink-0 text-[1.4em]" isHovered={hover} />
            )}
            {child}
          </>
        )}
      </Slottable>
    </Component>
  )
}
